import React, { useRef, useState, useEffect } from "react";
import {
  Box,
  Grid,
  Typography,
  Button,
  Divider,
  Autocomplete,
  TextField,
  Stack,
  Dialog,
} from "@mui/material";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import CloseIcon from "@mui/icons-material/Close";
import { getProject } from "../../services/project.service";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { DemoContainer } from "@mui/x-date-pickers/internals/demo";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import {
  postTask,
  updateTask,
} from "../../services/taskManagement.service";
import { ToastError, ToastSuccess } from "../../utils/Toaster/Toaster";
import Delete_Icon from "../../assets/images/Task_File_Delete_Icon.svg";
import AddIcon from "@mui/icons-material/Add";
import { useAuth } from "../../context/AuthProvider";
import { jwtDecode } from "jwt-decode";
import { STATUS, PRIORITY, ISSUETYPE } from "../../common/constant/constant";
import Bugs_Icon from "../../assets/images/bug.png";
import Task_Icon from "../../assets/images/Task_Icon.svg";
import { IoFlagOutline } from "react-icons/io5";

// Helper functions using your numeric constants
const getIssueTypeLabel = (issueTypeValue) => {
  const issueTypeMap = {
    [ISSUETYPE.BUG]: "Bugs",
    [ISSUETYPE.TASK]: "Task",
    [ISSUETYPE.TESTING]: "Testing",
    [ISSUETYPE.DESIGNING]: "Designing",
    [ISSUETYPE.OTHERS]: "Others",
    [ISSUETYPE.MEETING]: "Meeting",
    [ISSUETYPE.DEPLOYMENT]: "Deployment",
  };
  return issueTypeMap[issueTypeValue] || "Task";
};

const getIssueTypeValue = (label) => {
  const issueTypeMap = {
    Bugs: ISSUETYPE.BUG,
    Task: ISSUETYPE.TASK,
    Testing: ISSUETYPE.TESTING,
    Designing: ISSUETYPE.DESIGNING,
    Others: ISSUETYPE.OTHERS,
    Meeting: ISSUETYPE.MEETING,
    Deployment: ISSUETYPE.DEPLOYMENT,
  };
  return issueTypeMap.hasOwnProperty(label)
    ? issueTypeMap[label]
    : ISSUETYPE.TASK;
};

const AddTaskClient = (props) => {
  const { setOpenDrawer, formType, selectedData, GetData } = props;
  const disabled = formType === "view";
  const { accessToken } = useAuth();
  const formRef = useRef(null);
  const [projectData, setProjectData] = useState([]);
  const [openImageDialog, setOpenImageDialog] = useState(false);
  const [selectedImage, setSelectedImage] = useState("");
  const [shouldCloseOnSubmit, setShouldCloseOnSubmit] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // File management state
  const [existingFiles, setExistingFiles] = useState([]);
  const [newFiles, setNewFiles] = useState([]);
  const [deletedFiles, setDeletedFiles] = useState([]);

  const [initialValues, setInitialValues] = useState({
    taskType: "",
    title: "",
    description: "",
    project: "",
    priority: PRIORITY.MEDIUM,
    clientDeadlineDate: "",
  });

  const validationSchema = Yup.object().shape({
    taskType: Yup.string().required("Task Type is required"),
    title: Yup.string().required("Title is required"),
    description: Yup.string().required("Description is required"),
    project: Yup.string().required("Project is required"),
    priority: Yup.number().required("Priority is required"),
    clientDeadlineDate: Yup.string().required("Deadline Date is required"),
  });

  const getUserIdFromToken = () => {
    if (!accessToken) return null;
    try {
      const decoded = jwtDecode(accessToken);
      return decoded.id;
    } catch (error) {
      console.error("Invalid token", error);
      return null;
    }
  };

  const userId = getUserIdFromToken();

  const GetProjectData = async (clientId = userId) => {
    try {
      const params = clientId
        ? { isEnabled: true, clientId }
        : { isEnabled: true };
      const res = await getProject(params);

      if (res.data.data && Array.isArray(res.data.data)) {
        const configData = res.data.data.map((e) => {
          return {
            ...e,
            label: e.name,
          };
        });
        setProjectData(configData);
      } else {
        console.error("Response data is not in expected format:", res.data);
      }
    } catch (error) {
      console.error("Error fetching project data:", error);
    }
  };

  const taskOption = [
    {
      label: "Task",
      name: "Task",
      value: ISSUETYPE.TASK,
      icon: (
        <img
          src={Task_Icon}
          alt="Task_Icon"
          style={{
            height: "20px",
            width: "20px",
          }}
        />
      ),
    },
    {
      label: "Bugs",
      name: "Bugs",
      value: ISSUETYPE.BUG,
      icon: (
        <img
          src={Bugs_Icon}
          alt="Bugs_Icon"
          style={{
            height: "20px",
            width: "20px",
          }}
        />
      ),
    },
  ];

  const priorityOption = [
    {
      label: "Low",
      name: "Low",
      value: PRIORITY.LOW,
      icon: <IoFlagOutline size={16} color={"#21A600"} />,
    },
    {
      label: "Medium",
      name: "Medium",
      value: PRIORITY.MEDIUM,
      icon: <IoFlagOutline size={16} color={"#D7BB21"} />,
    },
    {
      label: "High",
      name: "High",
      value: PRIORITY.HIGH,
      icon: <IoFlagOutline size={16} color={"#D50707"} />,
    },
  ];

  useEffect(() => {
    GetProjectData();

    if (formType === "edit" || formType === "view") {
      const cleanDescription =
        selectedData.description?.replace(/<[^>]*>/g, "") || "";

      const taskTypeLabel = getIssueTypeLabel(selectedData?.taskType);

      setInitialValues({
        taskType: taskTypeLabel || "",
        title: selectedData.title || "",
        description: cleanDescription,
        project: selectedData.project || "",
        priority: selectedData.priority,
        clientDeadlineDate: selectedData.clientDeadlineDate || "",
      });

      if (selectedData.file) {
        const files = Array.isArray(selectedData.file)
          ? selectedData.file
          : [selectedData.file];
        setExistingFiles(files.filter((file) => file && file.location));
      }
    }
  }, [formType, selectedData]);

  const getFileType = (filename) => {
    if (!filename) return "other";
    const extension = filename.split(".").pop().toLowerCase();
    switch (extension) {
      case "jpg":
      case "jpeg":
      case "png":
      case "gif":
        return "image";
      case "mp4":
      case "webm":
      case "ogg":
        return "video";
      case "pdf":
        return "pdf";
      case "doc":
      case "docx":
        return "word";
      case "xls":
      case "xlsx":
        return "excel";
      default:
        return "other";
    }
  };

  const renderFile = (file) => {
    const fileName = file.fileName || file.name;
    const fileUrl = file.location || URL.createObjectURL(file);
    const fileType = getFileType(fileName);

    if (fileType === "video") {
      return (
        <video
          controls
          style={{ width: "180px", maxHeight: "100px", flex: "1 1 200px" }}
        >
          <source src={fileUrl} type={file.type} />
          Your browser does not support the video tag.
        </video>
      );
    }

    if (fileType === "image") {
      return (
        <img
          src={fileUrl}
          alt="Uploaded content"
          style={{
            width: "180px",
            maxHeight: "100px",
            flex: "1 1 200px",
            cursor: "pointer",
          }}
          onClick={() => handleImageClick(fileUrl)}
        />
      );
    }

    return (
      <div style={{ padding: "10px", width: "180px" }}>
        <div style={{ fontWeight: "bold", wordBreak: "break-word" }}>
          {fileName}
        </div>
        <div style={{ fontSize: "12px", color: "#666" }}>
          {fileType.toUpperCase()} file
        </div>
      </div>
    );
  };

  const handleImageClick = (imageUrl) => {
    setSelectedImage(imageUrl);
    setOpenImageDialog(true);
  };

  const fileInputRef = useRef(null);

  const handleAddIconClick = () => {
    fileInputRef.current.click();
  };

  const handleDeleteFile = (file) => {
    if (file._id) {
      // Existing file - mark for deletion
      setDeletedFiles([...deletedFiles, file._id]);
      setExistingFiles(existingFiles.filter((f) => f._id !== file._id));
    } else {
      // New file - just remove from the list
      setNewFiles(newFiles.filter((f) => f.name !== file.name));
    }
  };

  const handleFileChange = (e) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      const filesArray = Array.from(files);
      setNewFiles((prev) => [...prev, ...filesArray]);
    }
    // Reset the input value to allow selecting the same file again
    e.target.value = null;
  };

  const onSubmit = async (values, { resetForm }) => {
    setIsSaving(true);
    try {
      const formData = new FormData();
      const userId = getUserIdFromToken();

      // Convert display labels back to numeric values for backend
      const bodyData = {
        issueType: getIssueTypeValue(values.taskType),
        title: values.title,
        description: values.description,
        project: values.project,
        priority: values.priority,
        clientDeadlineDate: values.clientDeadlineDate
          ? dayjs(values.clientDeadlineDate).toISOString()
          : null,
        assignedBy: userId,
        status: STATUS.PENDING, // Using numeric status constant
        isCliend: true,
      };

      formData.append("bodyData", JSON.stringify(bodyData));

      // Append new files
      newFiles.forEach((file) => {
        formData.append("files", file);
      });

      // If editing, include deleted file IDs
      if (formType === "edit" && deletedFiles.length > 0) {
        formData.append("deletedFiles", JSON.stringify(deletedFiles));
      }

      const apiMethod =
        formType === "edit"
          ? () => updateTask(selectedData._id, formData)
          : () => postTask(formData);

      const res = await apiMethod();
      ToastSuccess(res.data.message);

      // Reset form and state
      resetForm();
      setNewFiles([]);
      setDeletedFiles([]);
      setExistingFiles([]);
      if (shouldCloseOnSubmit) {
        setOpenDrawer(false);
      }
      GetData();
    } catch (error) {
      ToastError(error?.response?.data?.message || "An error occurred");
      console.error("Submission error:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const displayFiles = [
    ...existingFiles.filter((file) => !deletedFiles.includes(file._id)),
    ...newFiles,
  ];

  return (
    <div className={`${"addRenderingPopup"} no-scrollbar`}>
      <Box className="addContainerHeader">
        <Typography className="headerTitle">
          {formType === "edit" ? "Edit" : formType === "view" ? "View" : "Add"}{" "}
          Task
        </Typography>
        <CloseIcon
          className="Close-Icon"
          onClick={() => setOpenDrawer(false)}
        />
      </Box>
      <Divider sx={{ opacity: 1 }} />
      <Formik
        initialValues={initialValues}
        enableReinitialize
        validationSchema={validationSchema}
        onSubmit={onSubmit}
        innerRef={formRef}
      >
        {({ touched, errors, values, setFieldValue }) => (
          <Form>
            <Box className="addContainerCenter" mb={1}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} mt={1}>
                  <div className="wsh-form-control">
                    <Typography className="inputLabel">
                      Project <span style={{ color: "red" }}>*</span>
                    </Typography>
                    <Autocomplete
                      options={projectData}
                      value={
                        projectData.find(
                          (option) => option._id === values.project
                        ) || null
                      }
                      disabled={disabled}
                      onChange={(e, v) => {
                        setFieldValue("project", v?._id);
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          focused={false}
                          placeholder="Select Project"
                          className="AutoComplete_InputBox"
                        />
                      )}
                    />
                    <div className="form-error">
                      {touched.project && errors.project && errors.project}
                    </div>
                  </div>
                </Grid>
                <Grid item xs={12} sm={6} mt={1}>
                  <div className="wsh-form-control">
                    <Typography className="inputLabel">
                      Task Type <span style={{ color: "red" }}>*</span>
                    </Typography>
                    <Autocomplete
                      options={taskOption}
                      value={
                        taskOption.find(
                          (option) => option.label === values.taskType
                        ) || null
                      }
                      disabled={disabled}
                      onChange={(e, v) => {
                        setFieldValue("taskType", v?.label);
                      }}
                      renderOption={(props, option) => (
                        <li {...props}>
                          <span
                            style={{
                              marginRight: "8px",
                              display: "flex",
                              alignItems: "center",
                            }}
                          >
                            {option.icon}
                          </span>
                          {option.label}
                        </li>
                      )}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          focused={false}
                          placeholder="Select Task Type"
                          className="AutoComplete_InputBox"
                        />
                      )}
                    />
                    <div className="form-error">
                      {touched.taskType && errors.taskType && errors.taskType}
                    </div>
                  </div>
                </Grid>
                <Grid item xs={12} sm={12} mt={1}>
                  <Typography className="inputLabel">
                    Title <span style={{ color: "red" }}>*</span>
                  </Typography>
                  <Field
                    name="title"
                    className="InputField"
                    placeholder="Enter Title"
                    disabled={disabled}
                  />
                  <div className="form-error">
                    {touched.title && errors.title && errors.title}
                  </div>
                </Grid>
                <Grid item xs={12} sm={12} mt={1}>
                  <Typography className="inputLabel">
                    Description <span style={{ color: "red" }}>*</span>
                  </Typography>
                  <Field
                    name="description"
                    as="textarea"
                    className="InputField"
                    placeholder="Enter Description"
                    disabled={disabled}
                    style={{ minHeight: "100px" }}
                  />
                  <div className="form-error">
                    {touched.description &&
                      errors.description &&
                      errors.description}
                  </div>
                </Grid>
                <Grid item xs={12} sm={6} mt={1}>
                  <div className="wsh-form-control">
                    <Typography className="inputLabel">
                      Priority <span style={{ color: "red" }}>*</span>
                    </Typography>
                    <Autocomplete
                      options={priorityOption}
                      value={
                        priorityOption.find(
                          (option) => option.value === values.priority
                        ) || null
                      }
                      disabled={disabled}
                      onChange={(e, v) => {
                        setFieldValue("priority", v?.value);
                      }}
                      renderOption={(props, option) => (
                        <li {...props}>
                          <span
                            style={{
                              marginRight: "8px",
                              display: "flex",
                              alignItems: "center",
                            }}
                          >
                            {option.icon}
                          </span>
                          {option.label}
                        </li>
                      )}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          focused={false}
                          placeholder="Select Priority"
                          className="AutoComplete_InputBox"
                        />
                      )}
                    />
                    <div className="form-error">
                      {touched.priority && errors.priority && errors.priority}
                    </div>
                  </div>
                </Grid>
                <Grid item xs={12} sm={6} mt={1}>
                  <Typography className="inputLabel">
                    DeadLine Date <span style={{ color: "red" }}>*</span>
                  </Typography>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DemoContainer components={["DatePicker", "DatePicker"]}>
                      <DatePicker
                        value={
                          values?.clientDeadlineDate
                            ? dayjs(values?.clientDeadlineDate)
                            : null
                        }
                        onChange={(e) =>
                          setFieldValue("clientDeadlineDate", dayjs(e.$d))
                        }
                        className="datePicker w-100"
                        disabled={disabled}
                        format="DD/MM/YYYY"
                        minDate={dayjs()}
                      />
                    </DemoContainer>
                  </LocalizationProvider>
                  <div className="form-error">
                    {touched.clientDeadlineDate &&
                      errors.clientDeadlineDate &&
                      errors.clientDeadlineDate}
                  </div>
                </Grid>

                <Grid item xs={12} sm={12} mt={1}>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography className="inputLabel">Attachments</Typography>
                    {!disabled && (
                      <AddIcon
                        onClick={handleAddIconClick}
                        sx={{ cursor: "pointer" }}
                      />
                    )}
                  </Stack>
                  <input
                    type="file"
                    hidden
                    ref={fileInputRef}
                    onChange={handleFileChange}
                    multiple
                  />
                  <Box
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 2,
                      mt: 1,
                    }}
                  >
                    {displayFiles.length > 0 ? (
                      displayFiles.map((file, index) => (
                        <Box
                          key={index}
                          sx={{
                            border: "1px solid #E4E4E7",
                            width: "181px",
                            position: "relative",
                            borderRadius: "4px",
                            overflow: "hidden",
                          }}
                        >
                          {renderFile(file)}
                          {!disabled && (
                            <img
                              src={Delete_Icon}
                              alt="Delete_Icon"
                              style={{
                                position: "absolute",
                                top: 10,
                                right: 10,
                                cursor: "pointer",
                                background: "white",
                                borderRadius: "5px",
                                padding: "2px",
                              }}
                              onClick={() => handleDeleteFile(file)}
                            />
                          )}
                        </Box>
                      ))
                    ) : (
                      <Typography>No Files Attached</Typography>
                    )}
                  </Box>
                </Grid>
              </Grid>
            </Box>
            <Divider sx={{ opacity: 1 }} />
            <Grid className="addboxFooter">
              <Button
                className="cancelButton"
                onClick={() => setOpenDrawer(false)}
              >
                Cancel
              </Button>
              {!disabled && (
                <>
                  <Button
                    type="submit"
                    className="saveButton"
                    disabled={isSaving}
                    onClick={() => {
                      setShouldCloseOnSubmit(false);
                    }}
                  >
                    {isSaving && !shouldCloseOnSubmit ? 'Saving...' : 'Save'}
                  </Button>
                  <Button
                    type="submit"
                    className="saveButton"
                    sx={{
                      width: "fit-content",
                    }}
                    disabled={isSaving}
                    onClick={() => {
                      setShouldCloseOnSubmit(true);
                    }}
                  >
                    {isSaving && shouldCloseOnSubmit ? 'Saving...' : 'Save and Exit'}
                  </Button>
                </>
              )}
            </Grid>
          </Form>
        )}
      </Formik>
      <Dialog
        open={openImageDialog}
        onClose={() => setOpenImageDialog(false)}
        maxWidth="lg"
        fullWidth
      >
        <img
          src={selectedImage}
          alt="Full Screen"
          style={{ width: "100%", height: "auto" }}
        />
      </Dialog>
    </div>
  );
};

export default AddTaskClient;
